import { StaticImageData } from 'next/image';

import { APIImageType, ImageWithMetadata, TransformedImageType } from '@types';

/**
 * Converts an API asset into Next.js `Image` component props.
 * If the `image` is a string, it constructs an object
 * with default metadata values, allowing for optional overrides using `metaProps`.
 *
 * @param {ImageType | string} image - The image source, which can be a URL string or an object of type `ImageType`.
 * @param {Partial<ImageWithMetadata['meta']>} [metaProps] - Optional properties to override the default metadata.
 * @returns {ImageWithMetadata} The `ImageWithMetadata` object, containing the image URL and associated metadata.
 */
export const imageMetaConverter = (
  image: APIImageType | APIImageType[] | undefined,
  metaProps?: Partial<ImageWithMetadata['meta']>,
  options?: { includeDimensions: boolean },
): TransformedImageType | null => {
  if (!image) {
    return null;
  }

  if (Array.isArray(image)) {
    return imageMetaConverter(image[0], metaProps, options);
  }

  if (typeof image === 'string') {
    return {
      src: image,
      alt: metaProps?.alt || metaProps?.name || '',
      placeholder: metaProps?.blurhash ? 'blur' : 'empty',
      blurDataURL: metaProps?.blurhash,
      width: options?.includeDimensions ? metaProps?.dimensions?.width : undefined,
      height: options?.includeDimensions ? metaProps?.dimensions?.height : undefined,
    };
  }

  if (!image?.url) {
    return null;
  }

  return {
    src: image.url,
    alt: image.meta.alt || image.meta.name || '',
    placeholder: image.meta.blurhash ? 'blur' : 'empty',
    blurDataURL: image.meta.blurhash,
    width: options?.includeDimensions ? image.meta.dimensions?.width : undefined,
    height: options?.includeDimensions ? image.meta.dimensions?.height : undefined,
  };
};

/**
 * Converts a Next.js `StaticImageData` to an `ImageWithMetadata` object.
 * The function maps the properties from the static image to the metadata format,
 * allowing for optional overrides using `metaProps`.
 *
 * @param {StaticImageData} staticImage - The static image data imported from a file.
 * @param {Partial<ImageWithMetadata['meta']>} [metaProps] - Optional properties to override the default metadata.
 * @returns {ImageWithMetadata} The `ImageWithMetadata` object, containing the image URL and associated metadata.
 */
export const staticImageConverter = (
  staticImage: StaticImageData,
  metaProps?: Partial<ImageWithMetadata['meta']>,
): ImageWithMetadata => {
  return {
    url: staticImage.src,
    meta: {
      name: metaProps?.name || '',
      blurhash: staticImage.blurDataURL,
      alt: metaProps?.alt || '',
      dimensions: { width: staticImage.width, height: staticImage.height },
    },
  };
};
